import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FixelLogo, FormContainer, Row, StyledForm, StyledFormWrapper, StyledInput, StyledLabel, StyledLeftForm, StyledSignIn, StyledStar } from "../auth/signIn/signin.style";
import Recommendation from "../commons/recommendation/Recommendation.component";
import { PoweredBy, PurchaseButton, StyledRightForm, Terms } from "./checkout.style";
import plansList from "../plans/plansList";
import { StyledInputGroupRow } from "../auth/signUp/signup.style";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StyledCardPrice } from "../plans/plans.style";
import { useAuthState } from "react-firebase-hooks/auth";
import Firebase from "../../services/firebase";
import { ErrorContainer, StyledPurchaseDetails, StyledTitle, SuccessContainer } from "../style/global.style";
import StateContext from "../../context/StateContext";
import { axiosWrapper } from "../../services/axiosService";
import { getPlansByMail, getSegmentsByMail, patchPlan } from "../../services/globalService";
import SmallLoader from "../../components/utils/SmallLoader";
import { sendEvent } from "../../services/analytics";
import { prepPlansMap, prepWithExtras } from "../../services/dataService";
import { captureException } from "services/sentry";

const Checkout = (props) => {
	const { planId } = useParams();
	const [plan, setPlan] = useState({});
	const history = useHistory();
	const stripe = useStripe();
	const elements = useElements();
	const [user] = useAuthState(Firebase.auth);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [coupon, setCoupon] = useState(props?.location?.coupon);
	const [loading, setLoading] = useState(false);
	const { customer, dispatch, ACTIONS } = useContext(StateContext);

	useEffect(() => {
		if (!props?.location?.plan) {
			const planObj = plansList.find((plan) => plan.arrayId === planId);
			setPlan(planObj);
		} else {
			setPlan(props.location.plan);
		}
	}, [planId, props.location.plan]);

	useEffect(() => {
		setCoupon(props.location.coupon);
	}, [props.location.coupon]);

	const submit = async (e) => {
		e.preventDefault();
		setLoading(true);
		try {
			const card = elements.getElement(CardElement);
			if (!stripe || !elements) {
				return;
			}

			const { error, paymentMethod } = await stripe.createPaymentMethod({
				type: "card",
				card: card,
				billing_details: {
					email: user.email
				}
			});
			if (error) {
				throw error;
			}
			if (!paymentMethod?.id) {
				throw new Error("no payment method ID from stripe.");
			}
			const purchaseObj = {
				mail: user.email,
				payment_method: "stripe",
				payment_method_id: paymentMethod.id,
				type: plan.name.replace("-", "_"),
				price: plan.price,
				name: coupon ? `${plan.name} ${coupon}` : plan.name,
				planId: plan.stripePlanId,
				coupon,
				cust_id: customer.cust_no
			};
			const { data } = await axiosWrapper({ method: "post", url: "/billing", data: purchaseObj });
			//payment intent success
			const { client_secret, status, plan: newPlan } = data;
			if (status === "requires_confirmation") {
				const stripeRequest = await stripe.confirmCardPayment(client_secret);
				if (stripeRequest.error) {
					throw stripeRequest.error;
				} else {
					// update plan with valid status
					const planRequest = await patchPlan(newPlan?.plan_id, { state: "Active" });
					if (!planRequest) throw new Error("There was a problem creating plan");
					const [newPlans, newSegments] = await Promise.all([
						// get plans and segments after plan update
						getPlansByMail({ mail: user.email }),
						getSegmentsByMail({ mail: user.email })
					]);
					const newPlansMap = prepPlansMap(newPlans.data);
					const { segMap: newSegMap } = prepWithExtras({ segments: newSegments.data });

					setError("");
					setSuccess("Thank you for your purchase, redirecting...");
					sendEvent({ category: "purchase", action: plan.name, label: plan.coupon || "", value: plan.price });
					dispatch({ type: ACTIONS.SET_STATE, payload: { plans: newPlans.data, plansMap: newPlansMap, segments: newSegments.data, segMap: newSegMap } });
					setTimeout(function () {
						history.push("/plans");
					}, 3000);
				}
			}
		} catch (e) {
			console.error("Checkout.component.js ~ submit ~ error:", e?.message || e?.data || e);
			setError(`There was a problem completing the payment. You can try again later`);
			captureException(e, "log", {}, { action: "submit", description: "failed completing purchase" }, { scope: "checkout.component.js" });
		} finally {
			setLoading(false);
		}
	};

	const onchange = (e) => {
		switch (e.target.name) {
			case "firstName":
				setFirstName(e.target.value);
				break;
			case "lastName":
				setLastName(e.target.value);
				break;
			default:
				return;
		}
	};

	const cardElementOptions = {
		style: {
			base: {
				background: "#F5F5F5",
				border: "1px solid #DBDBDB",
				boxSizing: "border-box",
				borderRadius: "2.03125px",
				padding: "4rem",
				width: "100%",
				marginBottom: ".8rem",
				color: "#000",
				fontFamily: "Poppins, sans-serif",
				fontSize: "16px"
			},
			invalid: {
				color: "#9e2146"
			}
		},
		disabled: loading,
		hidePostalCode: true
	};

	return (
		<StyledSignIn>
			<FixelLogo />
			<StyledFormWrapper>
				<StyledLeftForm>
					{error && <ErrorContainer>{error}</ErrorContainer>}
					{success && <SuccessContainer>{success}</SuccessContainer>}
					{!error && !success && <div></div>}
					<FormContainer>
						<StyledTitle>Payment details</StyledTitle>
						<StyledForm onSubmit={submit}>
							<StyledInputGroupRow>
								<div>
									<StyledLabel htmlFor="firstName">
										First Name
										<StyledStar>*</StyledStar>
									</StyledLabel>
									<StyledInput required disabled={loading} onChange={(e) => onchange(e)} type="text" name="firstName" placeholder="Jim" id="firstName" value={firstName} />
								</div>
								<div>
									<StyledLabel htmlFor="lastName">
										Last Name
										<StyledStar>*</StyledStar>
									</StyledLabel>
									<StyledInput required disabled={loading} onChange={(e) => onchange(e)} type="text" name="lastName" placeholder="Henson" id="lastName" value={lastName} />
								</div>
							</StyledInputGroupRow>
							<CardElement required options={cardElementOptions} className="card-element" />
							<Row>
								<PurchaseButton type="submit" disabled={loading}>
									{loading ? <SmallLoader inline={true} /> : "Submit payment"}
								</PurchaseButton>
							</Row>
						</StyledForm>

						<PoweredBy>Powered By Stripe</PoweredBy>
					</FormContainer>
				</StyledLeftForm>
				<StyledRightForm>
					<StyledPurchaseDetails>
						<h4>{plan.name}</h4>
						<StyledCardPrice>
							<span>{plan.price}$</span>
							<p>/month</p>
						</StyledCardPrice>
						<Terms>{plan.terms}</Terms>
					</StyledPurchaseDetails>
				</StyledRightForm>
			</StyledFormWrapper>
			<Recommendation theme={"full"} />
		</StyledSignIn>
	);
};

export default React.memo(Checkout);
